import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Replace with your Facebook Pixel ID
const FACEBOOK_PIXEL_ID = '852821163216580';

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: (...args: unknown[]) => void;
  }
}

// Initialize Facebook Pixel - deferred loading for performance
const initPixel = () => {
  if (window.fbq) return; // Already initialized

  const n = (window.fbq = function (...args: unknown[]) {
    // @ts-expect-error - fbq uses callMethod pattern
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  }) as typeof window.fbq & { push: typeof Array.prototype.push; loaded: boolean; version: string; queue: unknown[][] };
  
  if (!window._fbq) window._fbq = n;
  n.push = n.push;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];

  // Load the Facebook Pixel script with defer for better performance
  const script = document.createElement('script');
  script.async = true;
  script.defer = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  // Initialize with your Pixel ID
  window.fbq('init', FACEBOOK_PIXEL_ID);
};

// Track page views
export const trackPageView = () => {
  if (window.fbq) {
    window.fbq('track', 'PageView');
  }
};

// Track ViewContent event (when viewing a product)
export const trackViewContent = (
  productId: string,
  productName: string,
  price: number,
  currency = 'USD'
) => {
  if (window.fbq) {
    window.fbq('track', 'ViewContent', {
      content_name: productName,
      content_ids: [productId],
      content_type: 'product',
      value: price,
      currency,
    });
  }
};

// Track AddToCart event
export const trackAddToCart = (
  productId: string,
  productName: string,
  price: number,
  currency = 'USD'
) => {
  if (window.fbq) {
    window.fbq('track', 'AddToCart', {
      content_name: productName,
      content_ids: [productId],
      content_type: 'product',
      value: price,
      currency,
    });
  }
};

// Track InitiateCheckout event
export const trackInitiateCheckout = (
  value: number,
  currency: string,
  numItems: number,
  contentIds: string[]
) => {
  if (window.fbq) {
    window.fbq('track', 'InitiateCheckout', {
      content_ids: contentIds,
      content_type: 'product',
      num_items: numItems,
      value,
      currency,
    });
  }
};

// Track Purchase event
export const trackPurchase = (
  value: number,
  currency: string,
  contentIds: string[],
  contentType = 'product'
) => {
  if (window.fbq) {
    window.fbq('track', 'Purchase', {
      content_ids: contentIds,
      content_type: contentType,
      value,
      currency,
    });
  }
};

const FacebookPixel = () => {
  const location = useLocation();
  const initialized = useRef(false);

  // Initialize pixel on mount - deferred to not block initial render
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // Defer Facebook Pixel initialization until after page is interactive
    if ('requestIdleCallback' in window) {
      (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(() => {
        initPixel();
        trackPageView();
      });
    } else {
      // Fallback for browsers that don't support requestIdleCallback
      setTimeout(() => {
        initPixel();
        trackPageView();
      }, 2000);
    }
  }, []);

  // Track page views on route change
  useEffect(() => {
    if (window.fbq) {
      trackPageView();
    }
  }, [location.pathname]);

  return null; // This component doesn't render anything
};

export default FacebookPixel;
